<template>
  <Modal class="file-uploader-modal">
    <button
      type="button"
      class="btn-close"
      @click.stop="setActiveModal(null)">
      <i class="fas fa-times"></i>
    </button>
    <div class="file-uploader-container">
      <div class="file-uploader-header">
        <div class="file-uploader-title">
          <h5>อัปโหลดไฟล์</h5>
          <button
            class="btn btn-secondary"
            @click.stop="$refs.file.click()">
            <i class="fas fa-plus"></i> อัปโหลดเพิ่ม
          </button>
        </div>
      </div>
      <div>
        <div
          class="errorDialogue"
          v-if="hasError()">
          <div class="alert alert-danger">
            <button
              class="btn btn-clear-error"
              @click="clearErrors">
              <i class="fas fa-times"></i>
            </button>
            <p>มีข้อผิดพลาดเกิดขึ้น</p>
            <ul
              v-for="(value, key) in errors"
              :key="key">
              <li v-html="value"></li>
            </ul>
            <button
              class="btn btn-danger"
              v-if="failedToUpload.length"
              @click.stop="retry(null)">
              <i class="fas fa-redo-alt"></i> อัปโหลดไฟล์ที่มีปัญหาทั้งหมดอีกครั้ง
            </button>
            <button
              class="btn btn-danger"
              v-if="failedToUpload.length"
              @click.stop="clearErrors">
              <i class="fas fa-trash"></i> ลบไฟล์ที่มีปัญหาทั้งหมด
            </button>
          </div>
        </div>
      </div>
      <div
        class="file-browser-element"
        v-for="(file, index) in uploadedFiles"
        :key="index">
        <div
          :id="'file'+file.filename"
          class="file-info">
          <div class="info--icon">
            <i :class="getFileIconClass(file.type)"></i>
          </div>
          <div class="info--meta">
            <div>
              <p>{{file.name}}</p>
              <small>{{file.type.toUpperCase()}}</small>
              <small> ({{prettyFileSize(file.fileSize)}}) </small>
              <input
                class="input-name"
                type="text"
                :id="'name'+index"
                :disabled="!file.success"
                v-if="false"
                v-model="file.name">
            </div>
          </div>
          <div class="info--actions">
            <button
              v-if="hasError() && !file.success"
              class="btn btn-danger btn-retry"
              @click.stop="retry(file.filename)">
              <i class="fas fa-redo-alt"></i>
            </button>
            <button
              @click.stop="abort(file.filename, index)"
              v-if="file.uploadPercentage !== 100"
              class="btn btn-light btn-stop">
              <i class="far fa-stop-circle"></i>
            </button>
            <DoubleTabToConfirmButton
              @confirm="onDeleteUploadedFile(index)" />
          </div>
        </div>
        <div :class="`progress ${file.uploadPercentage === 100 || hasError() ? 'hide' : ''}`">
          <div
            class="loader"
            :style="{width: `${file.uploadPercentage}%`}">
          </div>
          <div class="glowing"></div>
        </div>
      </div>
      <div class="file-browser-bottom">
        <button
          type="button"
          class="btn btn-primary btn-lg"
          :disabled="!allFilesUploaded()"
          @click.stop="attachUploadedFiles">
          แนบไฟล์
        </button>
      </div>
    </div>
    <input
      class="hidden-input"
      type="file"
      ref="file"
      @change="onSelectFilesToUpload"
      multiple>
  </Modal>
</template>

<script>
import _ from 'lodash';
import { mapGetters, mapActions } from 'vuex';
import DoubleTabToConfirmButton from '@/components/DoubleTapToConfirmButton.vue';
import Modal from '@/components/Modal.vue';
import unitFormatter from '@/utils/unitFormatter';

import attachmentMixin from './attachmentMixin';
import { generateDefaultAttachmentItem } from './attachmentManagement';

export default {
  mixins: [attachmentMixin, unitFormatter],
  components: {
    Modal,
    DoubleTabToConfirmButton,
  },
  computed: {
    ...mapGetters([
      'attachment',
      'uploadedFiles',
      'failedToUpload',
    ]),
  },
  methods: {
    ...mapActions([
      'setActiveModal',
      'setAttachmentID',
      'inheritAttachment',
      'resetFileBrowser',
      'resetErrors',
      'clearAttachmentUploadingStates',
    ]),
    async attachUploadedFiles() {
      this.setActiveModal(null);
      const toBeAttached = _.map(this.uploadedFiles, (fileData) => {
        return generateDefaultAttachmentItem(fileData);
      });
      await this.updateFilesToAttachment(toBeAttached);
      this.clearAttachmentUploadingStates();
    },
  },
};
</script>

<style lang="scss" scoped>
@import './src/assets/scss/variables.scss';
@import './src/assets/scss/themes/components/progress-bar.scss';

.hidden-input {
  display: none;
}
.file-uploader-modal {
  .btn-close {
    z-index: 3;
    position: absolute;
    top: 16px;
    right: 10px;
    background: transparent;
    border: none;
    outline: none;
    transition: color 250ms ease;
    color: $gray-700;
    &:hover {
      color: $red;
    }
  }
  .file-uploader-container {
    padding: 20px;
    h5 {
      margin-bottom: 20px;
    }
    input {
      margin-bottom: 10px;
    }
    .alert-danger {
      position: relative;
      border-radius: 0.25rem;
      p {
        margin-top: 10px;
      }
      .btn-clear-error {
        cursor: pointer;
        position: absolute;
        top: 16px;
        right: 0;
      }
      .btn-danger {
        margin-bottom: 4px;
      }
    }
    .file-uploader-title {
      display: flex;
      flex-flow: row;
      button {
        margin-left: 10px;
        margin-top: -6px;
        padding: 9px;
        font-size: 11px;
        height: 32px;
      }
    }
    .file-browser-element {
      .file-info {
        display: flex;
        flex-flow: row;
        padding: 10px 2px;
        transition: background 250ms ease;
        .info--icon {
          width: 26px;
          display: flex;
          justify-content: center;
          align-items: center;
          flex-flow: column;
          i {
            font-size: 38px;
          }
          button {
            font-size: 8px;
            display: flex;
            justify-content: center;
            width: 100%;
            color: #FFF;
            height: 26px;
            margin: 4px 0 0 0;
            i {
              font-size: 12px;
              margin-bottom: 2px;
              margin-right: 2px;
            }
          }
        }
        .info--meta {
          display: flex;
          justify-content: center;
          align-items: flex-start;
          flex-flow: column;
          flex: 1;
          div {
            width: 97%;
            margin: auto;
          }
          p {
            margin: 0;
            i {
              margin-left: 4px;
            }
          }
          .input-name {
            border: none;
            color: $gray-600;
            border-bottom: 1px solid $gray-600;
            width: 100%;
            margin-bottom: 10px;
          }
        }
        .info--actions {
          position: relative;
          display: flex;
          flex-flow: row;
          button {
            margin-right: 6px;
            padding: 0 14px;
          }
        }
      }
    }
    .file-browser-add-more-container {
      cursor: pointer;
      width: 100%;
      height: 50px;
      display: flex;
      align-items: center;
      justify-content: center;

      p {
        cursor: pointer;
        margin: 0;
      }
    }
    .file-browser-bottom {
      margin-top: 20px;
      text-align: right;
      button {
        font-size: 12px;
        height: 38px;
      }
      .btn-delete {
        margin-right: 10px;
      }
    }
  }
}
</style>
